import React, { useCallback, useEffect, useMemo, useState } from "react";
import swal from "sweetalert";
import PageTitle from "../../layouts/PageTitle";

// Import EditUserProfile components
import UserHeader from "./EditUserProfile/UserHeader";
import UserDetailsTable from "./EditUserProfile/UserDetailsTable";
import EditSections from "./EditUserProfile/EditSections";
import OtpModal from "./EditUserProfile/OtpModal";

import {
  getAllUserProfileList,
  updateUserProfileDetails,
  uploadUserDocument,
  sendMobileOtp,
  verifyMobileOtp,
  updateUserMobileNumber,
  sendEmailOtp,
  verifyEmailOtp,
  updateUserEmail,
  checkFieldUpdate,
  deleteUploadDocument,
} from "../../../services/userApiService/userService";
import { validateSections } from "../../../utils/validator";

// Alert wrapper
const notify = (type, message) =>
  swal(type === "error" ? "Error" : "Success", message, type);

// Default section field values
const getDefaultSectionValues = (userData = {}, entity = {}) => ({
  first_name: userData.first_name || "",
  middle_name: userData.middle_name || "",
  last_name: userData.last_name || "",
  dob: entity.dob || "",
  district: entity.city || "",
  city: entity.city || "",
  pincode: entity.pincode || "",
  landmark: entity.landmark || "",
  address: entity.address || "",
  mobile: entity.mobile || "",
  email: userData.email || "",
  kit_size: entity.kit_size || "",
  shoe_size: entity.shoe_size || "",
  bank_name: entity.bank_name || "",
  sport: entity.ref_sport?.sport_id || entity.sport_id || "",
  account_holder_name: entity.account_holder_name || "",
  account_number: entity.bank_account_number || "",
  branch_name: entity.branch_name || "",
  ifsc_code: entity.ifsc_code || "",
  file: null,
  fileUrl: "",

  // Identification
  identification_type: entity.identification_type || "",
  identification_number: entity.identification_number || "",

  // Education
  highest_qualification: entity.highest_qualification || "",
});

// Map section name to doc_name
const getDocNameFromField = (field = "") => {
  const map = {
    name: "name_image",
    address: "address_image",
    bank_details: "cancelled_cheque_or_passbook",
    kit_size: "kit_size_image",
    educational_details: "education_certificate",
    kit_size: "kit_size_image",
    kit_size: "kit_size_image",
    dob: "dob_image",
    profile: "profile_image",
    profile: "profile_image",
    identification: "identification_document",
    sports_achievement: "sports_achievement",
    sport: "sports_achievement",
    marital_status: "maritial_status",
    center_details: "center_doc",
    contact_person: "center_contact_person_name_image",
  };
  return map[field.toLowerCase()] || "supportive_document";
};

const educationLevels = [
  { label: "10TH", key: "tenth" },
  { label: "12TH", key: "twelfth" },
  { label: "GRADUATE", key: "ug" },
  { label: "POST GRADUATE", key: "pg" },
  { label: "PHD", key: "phd" },
  { label: "TRANSFER CERTIFICATE", key: "tc" },
];

const EditProfileRequest = () => {
  const [userData, setUserData] = useState({});
  const [userProfileImage, setUserProfileImage] = useState(null);
  const [sections, setSections] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [mobileForm, setMobileForm] = useState({ prev: "", next: "", otp: "", otpSent: false });
  const [emailForm, setEmailForm] = useState({ prev: "", next: "", otp: "", otpSent: false });

  const id = localStorage.getItem("id");
  const userCategoryMain = localStorage.getItem("user_category_main");
  const categoryType = localStorage.getItem("category_type");

  const entity = useMemo(() => {
    if (String(userCategoryMain) === "2") return userData?.organization_profile || {};
    return userData?.indivisual || {};
  }, [userData, userCategoryMain]);

  const docs = useMemo(() => userData?.indivisual_docs || [], [userData]);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const res = await getAllUserProfileList(userCategoryMain, id);
      const data = res?.data?.data || {};
      setUserData(data);
      setUserProfileImage(data?.indivisual_docs?.[0]?.doc_file || null);

      const defaultEntity = String(userCategoryMain) === "2" ? data?.organization_profile : data?.indivisual;
      setSections([{ id: Date.now(), field: "", values: getDefaultSectionValues(data, defaultEntity || {}) }]);
    } catch (err) {
      console.error("Failed fetching user data", err);
      notify("error", "Could not load user data");
    }
  };

  const addSection = () =>
    setSections((prev) => [
      ...prev,
      { id: Date.now(), field: "", values: getDefaultSectionValues(userData, entity) },
    ]);

  const removeSection = (id) => setSections((prev) => prev.filter((s) => s.id !== id));

  const onFieldChange = async (id, field) => {
    if (field === "name" || field === "dob") {
      try {
        const res = await checkFieldUpdate(field);
        // Assuming response structure: { status: true/false, message: "..." }
        // User said: "if it will get response is false show the message which ai gives"
        // Adjust based on actual response property if needed. Usually res.data.status.
        if (!res?.data?.status) {
          notify("error", res?.data?.message || `You are not allowed to edit ${field}`);
          // Reset the select if needed, or just don't update the section, so it stays on previous value or empty?
          // If the select component is controlled by section.field, it won't change if we don't update state.
          // But if the user selected it in the UI, does the UI wait for this?
          // If EditSections uses a controlled input, it depends on `sections` state.
          // If we return here, the state remains, so the UI should revert or stay as is.
          return;
        }
      } catch (err) {
        console.error("Check field update failed", err);
        notify("error", "Failed to verify edit permission");
        return;
      }
    }

    setSections((prev) =>
      prev.map((s) => {
        if (s.id !== id) return s;

        const newValues = { ...getDefaultSectionValues(userData, entity) };

        switch (field) {
          case "name":
            newValues.first_name = userData?.first_name || "";
            newValues.middle_name = userData?.middle_name || "";
            newValues.last_name = userData?.last_name || "";
            break;
          case "dob":
            newValues.dob = entity?.dob || "";
            break;
          case "address":
            newValues.district = entity?.city || "";
            newValues.district_name = entity?.city_name || "";
            newValues.pincode = entity?.pincode || "";
            newValues.landmark = entity?.landmark || "";
            newValues.address = entity?.address || "";
            break;
          case "kit_size":
            newValues.kit_size = entity?.kit_size || "";
            break;

          case "bank_details":
            newValues.bank_name = entity?.bank_name || "";
            newValues.account_holder_name = entity?.account_holder_name || "";
            newValues.account_number = entity?.bank_account_number || "";
            newValues.branch_name = entity?.branch_name || "";
            newValues.ifsc_code = entity?.ifsc_code || "";
            break;
          case "profile":
            newValues.photofileUrl = userProfileImage || "";
            break;
          case "educational_details":
            newValues.highest_qualification = entity?.highest_qualification || "";
            break;
          case "center_details":
            if (String(userCategoryMain) !== "2") {
              newValues.district = userData?.organization_profile?.city || "";
              newValues.district_name = userData?.organization_profile?.city_name || "";
              newValues.center_type = userData?.organization_profile?.parent?.user_category_types_id || "";
              newValues.center_name = userData?.orgnization_id || "";
            }
            break;
          case "center_name":
            newValues.center_name = entity?.name_of_organization || userData?.name_of_legel_entity || "";
            break;
          case "contact_person":
            newValues.contact_person = userData?.organization?.contact_person_name || "";
            break;
          case "center_type":
            newValues.center_type = userData?.category_type ?? entity?.parent?.user_category_types_id ?? "";
            newValues.center_type_name = userData?.center_type || entity?.parent?.category_name || entity?.parent?.type_name || entity?.parent?.center_name || "";
            break;
          case "center_district":
            newValues.district = entity?.city || userData?.organization?.city || userData?.organization_profile?.city || "";
            newValues.district_name = entity?.city_name || userData?.organization?.city_name || userData?.organization_profile?.city_name || "";
            break;
          case "contact_number":
            newValues.contact_number = entity?.contact_person_contact || entity?.mobile || "";
            break;
          case "identification":
            newValues.identification_type = entity?.identification_type || "";
            newValues.identification_number = entity?.identification_number || "";
            break;
          case "sports_achievement":
            newValues.sports_achievement = entity?.sports_achievement || "";
            break;
          case "sport":
            newValues.sport = entity?.ref_sport?.sport_id || entity?.sport_id || "";
            break;
          case "marital_status":
            newValues.marital_status = entity?.maritial_status || "";
            break;
          default:
            break;
        }

        return { ...s, field, values: newValues };
      })
    );
  };

  const onValueChange = (id, key, value) =>
    setSections((prev) =>
      prev.map((s) =>
        s.id === id ? { ...s, values: { ...s.values, [key]: value } } : s
      )
    );

  const handleFileUpload = async (id, file, isPhoto = false, subType = null) => {
    if (!file) return;

    // Validate file type - only allow PNG, JPG, JPEG images
    const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
    const allowedExtensions = [".png", ".jpg", ".jpeg"];
    const fileExtension = file.name ? file.name.substring(file.name.lastIndexOf('.')).toLowerCase() : "";

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      notify("error", "Only image files (PNG, JPG, JPEG) are allowed. Please upload a valid image.");
      return;
    }

    const section = sections.find((s) => s.id === id);
    if (!section?.field && !isPhoto) {
      notify("error", "Select a field before uploading a document");
      return;
    }

    if (section?.field === "educational_details" && !subType) {
      notify("error", "Please select a qualification first");
      return;
    }

    if (section?.field === "identification" && !subType) {
      notify("error", "Please select an identification type first");
      return;
    }

    const docName = subType
      ? subType
      : isPhoto
        ? "profile_image"
        : getDocNameFromField(section.field);

    if (subType && section.field === "educational_details") {
      // Set uploading state for specific education row
      setSections((prev) =>
        prev.map((s) =>
          s.id === id
            ? {
              ...s,
              values: {
                ...s.values,
                education_uploads: {
                  ...s.values.education_uploads,
                  [subType]: {
                    ...(s.values.education_uploads?.[subType] || {}),
                    uploading: true,
                    error: null
                  }
                }
              },
            }
            : s
        )
      );
    } else {
      // Existing single file upload state logic
      setSections((prev) =>
        prev.map((s) =>
          s.id === id ? { ...s, values: { ...s.values, uploading: true, uploadError: false, uploadSuccess: false } } : s
        )
      );
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("user_id", userData.id || id);
    formData.append("doc_name", docName);

    try {
      const res = await uploadUserDocument(formData);
      const { id: docId, doc_file } = res?.data?.data || {};
      if (docId) {
        setSections((prev) =>
          prev.map((s) => {
            if (s.id !== id) return s;

            if (subType && s.field === "educational_details") {
              return {
                ...s,
                values: {
                  ...s.values,
                  education_uploads: {
                    ...s.values.education_uploads,
                    [subType]: {
                      uploading: false,
                      success: true,
                      docId,
                      doc_file,
                      file
                    }
                  }
                }
              };
            }

            return {
              ...s,
              values: {
                ...s.values,
                [isPhoto ? "photoDocumentId" : "documentId"]: docId,
                [isPhoto ? "photofileUrl" : "fileUrl"]: doc_file,
                [isPhoto ? "photofile" : "file"]: file,
                [`${s.field.toLowerCase()}_document`]: file,
                ...(s.field === "bank_details" ? { cancelled_cheque_or_passbook: file } : {}),
                uploading: false,
                uploadSuccess: true
              },
            };
          })
        );
        notify("success", `${subType ? "Certificate" : (isPhoto ? "Photo" : "Document")} uploaded successfully`);
      } else throw new Error("Invalid upload response");
    } catch (err) {
      console.error("Upload failed", err);

      // Extract error message from API response
      let errorMessage = "File upload failed. Please try again.";

      if (err?.response?.data?.message) {
        const msg = err.response.data.message;

        if (typeof msg === 'string') {
          errorMessage = msg;
        } else if (typeof msg === 'object') {
          // Handle error format like {"file":["The file field must not be greater than 2048 kilobytes."]}
          const firstKey = Object.keys(msg)[0];
          const firstError = msg[firstKey];
          errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
        }
      }

      notify("error", errorMessage);
      setSections((prev) =>
        prev.map((s) => {
          if (s.id !== id) return s;
          if (subType && s.field === "educational_details") {
            return {
              ...s,
              values: {
                ...s.values,
                education_uploads: {
                  ...s.values.education_uploads,
                  [subType]: {
                    ...(s.values.education_uploads?.[subType] || {}),
                    uploading: false,
                    error: true
                  }
                }
              }
            };
          }
          return { ...s, values: { ...s.values, uploading: false, uploadError: true } };
        })
      );
    }
  };

  const handleRemoveFile = async (id, subType = null) => {
    const section = sections.find((s) => s.id === id);
    if (!section) return;

    let docIdToDelete = null;

    // Determine which document ID to delete
    if (subType && section.field === "educational_details") {
      docIdToDelete = section.values.education_uploads?.[subType]?.docId;
    } else {
      // For standard single-file fields, check both documentId and photoDocumentId
      docIdToDelete = section.values.documentId || section.values.photoDocumentId;
    }

    // Call the delete API if we have a document ID
    if (docIdToDelete) {
      try {
        await deleteUploadDocument(docIdToDelete);
        notify("success", "Document deleted successfully");
      } catch (err) {
        console.error("Failed to delete document:", err);
        notify("error", "Failed to delete document from server");
        return; // Don't proceed with state update if API call failed
      }
    }

    // Update state to remove the file
    setSections((prev) =>
      prev.map((s) => {
        if (s.id !== id) return s;

        if (subType && s.field === "educational_details") {
          const newUploads = { ...s.values.education_uploads };
          delete newUploads[subType]; // Completely remove the entry for this subtype
          return {
            ...s,
            values: {
              ...s.values,
              education_uploads: newUploads
            }
          };
        }

        // For standard single-file fields
        const newValues = { ...s.values };
        // Reset all file-related fields
        newValues.file = null;
        newValues.fileUrl = "";
        newValues.documentId = null;
        newValues.photoDocumentId = null; // In case it was a photo
        newValues.photofileUrl = "";
        newValues.photofile = null;
        // Crucial: Reset status flags
        newValues.uploading = false;
        newValues.uploadSuccess = false;
        newValues.uploadError = false;

        // Also clean up specific field document keys if they exist
        if (s.field) {
          delete newValues[`${s.field.toLowerCase()}_document`];
          if (s.field === "bank_details") delete newValues.cancelled_cheque_or_passbook;
        }

        return { ...s, values: newValues };
      })
    );
  };
  const onSubmit = async (e) => {
    e.preventDefault();

    const selected = sections.filter((s) => !!s.field);
    const validation = validateSections(selected, categoryType);
    if (!validation.valid) {
      setValidationErrors(validation.errorsById || {});
      return;
    }
    setValidationErrors({});

    try {
      setIsSubmitting(true);

      const payload = {
        user_id: Number(id || ""),
        meta_value: {},
        status: "pending",
        comment: "",
      };

      const attach_document = {};

      for (const s of selected) {
        const f = s.field.toLowerCase();
        const v = s.values || {};
        console.log(v);

        if (v.documentId) {
          const docKey = f === "identification" ? v.identification_type : getDocNameFromField(f);
          if (docKey) attach_document[docKey] = v.documentId;
        }
        if (v.photoDocumentId) attach_document.profile_image = v.photoDocumentId;

        switch (f) {
          case "name":
            payload.meta_value.name = {
              first_name: v.first_name,
              middle_name: v.middle_name,
              last_name: v.last_name,
              name_image: attach_document.name_image || ""
            };
            break;
          case "address":
            payload.meta_value.address = {
              city: v.district,
              city_name: v.district_name || entity?.city_name || "",
              pincode: v.pincode,
              landmark: v.landmark,
              address: v.address,
            };
            break;
          case "dob":
            payload.meta_value.date_of_birth = { dob: v.dob };
            break;
          case "bank_details":
            payload.meta_value.bank_details = {
              bank_name: v.bank_name,
              account_holder_name: v.account_holder_name,
              bank_account_number: v.account_number,
              branch_name: v.branch_name,
              ifsc_code: v.ifsc_code,
              cancelled_cheque_or_passbook_id:
                attach_document.cancelled_cheque_or_passbook || v.cancelled_cheque_or_passbook_id,
            };
            break;
          case "kit_size":
            payload.meta_value.kit_size = {
              kit_size: v.kit_size,
              kit_name: v.kit_name || "", // Add kit name

            };
            break;
          case "profile":
            payload.meta_value.profile_image = {
              profile_image: attach_document.profile_image || v.profile_image,
            };
            break;
          case "educational_details":
            {
              const qual = v.highest_qualification;
              if (qual) {
                const uploadData = v.education_uploads?.[qual];
                // If there's a new upload, use its docId and add to attach_document
                if (uploadData?.docId) {
                  attach_document[qual] = uploadData.docId;
                  payload.meta_value.education = {
                    [qual]: uploadData.docId
                  };
                } else {
                  // If no new upload, we might want to check if there is an existing one?
                  // For now, we follow the previous pattern but structurally updated.
                  // If doc_id is null, it's null.
                  payload.meta_value.education = {
                    [qual]: { doc_id: null }
                  };
                }
              }
            }
            break;
          case "center_details":
            if (String(userCategoryMain) === "2") {
              // Fallback
              payload.meta_value.center_details = {
                name_of_organization: v.center_name,
                contact_person_name: v.contact_person,
                mobile: v.contact_number,
              };
            } else {
              const centerDetails = {
                center_district_name: v.district_name || userData?.organization_profile?.city_name || "",
              };
              if (v.center_name) {
                centerDetails.orgnization_id = v.center_name;
                centerDetails.center_type_name = v.center_type_name || ""; // Add center type name
                // This is actually the ID
                centerDetails.center_name = v.center_name_text || ""; // Add center name text

              }
              payload.meta_value.center_details = centerDetails;
            }
            break;
          case "center_name":
            payload.meta_value.name = {
              name_of_legel_entity: v.center_name || entity?.name_of_organization,
            };
            break;
          case "contact_person":
            payload.meta_value.contact_person_details = {
              contact_person_name: v.contact_person || entity?.contact_person_name,
              center_contact_person_name_image: attach_document?.center_contact_person_name_image || "",
            };
            break;
          case "center_type":
            if (String(userCategoryMain) === "2") {
              payload.meta_value.center_details = {
                category_type: v.center_type,
                center_type_name: v.center_type_name || "",
              };
            }
            break;
          case "center_district":
            if (String(userCategoryMain) === "2") {
              payload.meta_value.center_city_details = {
                city: v.district,
                city_name: v.district_name || entity?.city_name || "",
              };
            }
            break;
          case "contact_number":
            payload.meta_value.center_details = {
              contact_person_name: v.contact_person || entity?.contact_person_name,
              mobile: v.contact_number || entity?.mobile,
            };
            break;


          case "identification":
            payload.meta_value.identification = {
              identity_type: v.identification_type,
              identity_card: v.identification_number,
              identity_doc: {
                [v.identification_type]: attach_document[v.identification_type] || v.identification_document_id
              }
            };
            break;
          case "sports_achievement":
            payload.meta_value.sports_achievement = {
              sports_achievement: attach_document.sports_achievement
            };
            break;
          case "sport":
            // User requested: "sports_achievent:{ sports_achievement:, sport_id:, }"
            // Even for coaches, use this structure.
            // Since there is no upload for 'sport' section for coaches, attach_document.sports_achievement might be undefined.
            // We use || "" to treat it as empty string if not present.
            payload.meta_value.sports_achievement = {
              sports_achievement: attach_document.sports_achievement || "",
              sport_id: v.sport,
              sport_name: v.sport_name || ""
            };
            break;
          case "marital_status":
            payload.meta_value.maritial_status = { maritial_status: v.marital_status };
            break;
          default:
            break;
        }
      }

      if (Object.keys(attach_document).length)
        payload.attach_document = attach_document;

      const apiPayload = {
        user_id: payload.user_id,
        meta_value: JSON.stringify(payload.meta_value),
        status: payload.status,
        comment: payload.comment,
        attach_document: JSON.stringify(payload.attach_document || {})
      };

      const response = await updateUserProfileDetails(apiPayload);
      if (response?.data?.status) {
        notify("success", "Profile updated successfully");
        // Reset form
        setSections([{ id: Date.now(), field: "", values: getDefaultSectionValues(userData, entity) }]);
        setValidationErrors({});
      } else {
        notify("error", `${response?.data?.message}`);
      }
    } catch (err) {
      console.error("Error submitting update", err);
      notify("error", "Failed to update profile");
    } finally {
      setIsSubmitting(false);
    }
  };

  const sendOtp = async (type, value) => {
    if (!value) return notify("error", `Enter a new ${type}`);
    try {
      console.log("Sending OTP to", type, value);
      const fn = type === "mobile" ? sendMobileOtp : sendEmailOtp;
      const res = await fn(value);

      if (res?.data?.status) {
        notify("success", `OTP sent to your ${type}`);
        if (type === "mobile") setMobileForm((f) => ({ ...f, otpSent: true }));
        else setEmailForm((f) => ({ ...f, otpSent: true }));
      } else {
        // Extract error message from response
        let errorMessage = "Failed to send OTP";

        if (res?.data?.message) {
          const msg = res.data.message;

          if (typeof msg === 'string') {
            errorMessage = msg;
          } else if (typeof msg === 'object') {
            // Extract from {"email":["Email already exist"]}
            const firstKey = Object.keys(msg)[0];
            const firstError = msg[firstKey];
            errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
          }
        }

        notify("error", errorMessage);
      }
    } catch (error) {
      // Handle network errors or exceptions
      let errorMessage = "Something went wrong while sending OTP";

      if (error?.response?.data?.message) {
        const msg = error.response.data.message;

        if (typeof msg === 'string') {
          errorMessage = msg;
        } else if (typeof msg === 'object') {
          const firstKey = Object.keys(msg)[0];
          const firstError = msg[firstKey];
          errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
        }
      }

      notify("error", errorMessage);
    }
  };

  const verifyAndUpdate = async (type, value, otp) => {
    if (!value || !otp) return notify("error", "Enter both value and OTP");

    // const verifyFn = type === "mobile" ? verifyMobileOtp : verifyEmailOtp;
    const updateFn = type === "mobile" ? updateUserMobileNumber : updateUserEmail;

    try {
      // const verifyRes = await verifyFn(value, otp);
      // if (verifyRes?.data?.status || verifyRes?.data?.success) {
      const updateRes = await updateFn(value, otp);
      if (updateRes?.data?.status || updateRes?.data?.success) {
        notify("success", `${type} updated successfully`);
        type === "mobile" ? setShowMobileModal(false) : setShowEmailModal(false);
        fetchUserData();
      } else notify("error", updateRes?.data?.message || "Update failed");
      // } else notify("error", "OTP verification failed");
    } catch (err) {
      console.error(err);
      notify("error", "Error verifying OTP");
    }
  };

  const openMobileModal = () => {
    const mobile = String(userCategoryMain) === "2" ? userData?.organization?.mobile : entity?.mobile;
    setMobileForm({
      prev: mobile || "",
      next: "",
      otp: "",
      otpSent: false,
    });
    setShowMobileModal(true);
  };

  const openEmailModal = () => {
    setEmailForm({
      prev: userData?.email || "",
      next: "",
      otp: "",
      otpSent: false,
    });
    setShowEmailModal(true);
  };

  useEffect(() => {
    const mobile = String(userCategoryMain) === "2" ? userData?.organization?.contact_person_contact : entity?.mobile;
    setMobileForm((prev) => ({ ...prev, prev: mobile || "" }));
    setEmailForm((prev) => ({ ...prev, prev: userData?.email || "" }));
  }, [entity?.mobile, userData?.email, categoryType, userData?.organization]);

  const fullName = [userData.first_name, userData.middle_name, userData.last_name]
    .filter(Boolean)
    .join(" ");
  
  return (
    <>
      <PageTitle activeMenu="Edit Profile Request" motherMenu="Account Management" />

      <section>
        <div className="card px-4 py-3 mb-3">
          <UserHeader
            userProfileImage={userProfileImage}
            userData={userData}
            fullName={fullName}
            onBack={null}
            categoryType={categoryType}
          />
          <UserDetailsTable
            userData={userData}
            entity={entity}
            docs={docs}
            onEditMobile={openMobileModal}
            onEditEmail={openEmailModal}
            categoryType={categoryType}
            userCategoryMain={userCategoryMain}
          />
        </div>

        <EditSections
          userData={userData}
          userProfileImage={userProfileImage}
          sections={sections}
          onAddSection={addSection}
          onRemoveSection={removeSection}
          onFieldChange={onFieldChange}
          onValueChange={onValueChange}
          onFileChange={(id, f, subType) => handleFileUpload(id, f, false, subType)}
          onPhotoFileChange={(id, f) => handleFileUpload(id, f, true)}
          onRemoveFile={handleRemoveFile}
          onSubmit={onSubmit}

          validationErrors={validationErrors}
          clearValidationError={(id) =>
            setValidationErrors((prev) => {
              const copy = { ...prev };
              delete copy[id];
              return copy;
            })
          }
          categoryType={categoryType}
          userCategoryMain={userCategoryMain}
        />
      </section>

      {/* Mobile OTP Modal */}
      <OtpModal
        show={showMobileModal}
        onHide={() => setShowMobileModal(false)}
        title="Edit Mobile Number"
        helpText="Please enter valid mobile number"
        prevLabel="Previous Number"
        nextLabel="New Number"
        form={mobileForm}
        setForm={setMobileForm}
        onGetOtp={() => sendOtp("mobile", mobileForm.next)}
        onSubmit={(e) => {
          e.preventDefault();
          verifyAndUpdate("mobile", mobileForm.next, mobileForm.otp);
        }}
      />

      {/* Email OTP Modal */}
      <OtpModal
        show={showEmailModal}
        onHide={() => setShowEmailModal(false)}
        title="Edit Email"
        helpText="Please enter valid email"
        prevLabel="Previous Email"
        nextLabel="New Email"
        form={emailForm}
        setForm={setEmailForm}
        onGetOtp={() => sendOtp("email", emailForm.next)}
        onSubmit={(e) => {
          e.preventDefault();
          verifyAndUpdate("email", emailForm.next, emailForm.otp);
        }}
      />
    </>
  );
};

export default EditProfileRequest;